import {v3, quat} from "./short3.js";
import Maths from "oc-commons/Maths.js";

// statische Hilfsvariablen, die in einigen Methoden benutzt werden
const edge = new THREE.Vector3();
const normal = new THREE.Vector3();
const EPSILON = 0.000001;
const default_epsilon_orthogonal = Math.cos((90-1)*Math.PI/180);
const default_epsilon_parallel = Math.cos(1*Math.PI/180);

/**
 * Statische Utility-Klasse zu 3D-Mathematik
 */
export default class Maths3 {

    /**
     * erzeugt zu einem gegebenen Koordinatensystem (definiert durch y- und z-Achse)
     * eine Matrix, die das Standard-Koordinatensystem in das gegebenen Koordinatensystem transformiert
     */
    static orientationMatrix(yAxis, zAxis) {
        var xAxis = v3().crossVectors(yAxis, zAxis);
        return new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
    }

    /**
     * verschiebt ein Object und gibt es zurück
     */
    static addPosition(object, xOrVector, y, z) {
        if (object) {
            if (xOrVector instanceof THREE.Vector3) {
                object.position.add(xOrVector);
            } else {
                object.position.add(v3(xOrVector, y, z));
            }
        }
        return object;
    };

    /**
     * Führt Math.sign für jede Komponente des Vectors aus
     */
    static signum(vector3) {
        vector3.x = Math.sign(vector3.x);
        vector3.y = Math.sign(vector3.y);
        vector3.z = Math.sign(vector3.z);
        return vector3;
    }

    /**
     * gibt true zurück, wenn der Vector existiert und jede Komponente eine endliche Zahl ist
     */
    static vectorIsFinite(v) {
        return v && !Maths.notFinite(v.x) && !Maths.notFinite(v.y) && !Maths.notFinite(v.z);
    };


    /**
     * gibt die minimale Länge einer Dimension eines Vectors zurück
     */
    static minAxis(vector) {
        return Math.min(vector.x, Math.min(vector.y, vector.z));
    }

    /**
     * gibt die maximale Länge einer Dimension eines Vectors zurück
     */
    static maxAxis(vector) {
        return Math.max(vector.x, Math.max(vector.y, vector.z));
    }


    /**
     * @deprecated use THREE.Spherical instead!
     * Wandelt einen Vector in Polarkoordinaten um.
     * Spherical Vector in ISO (physical) Convention
     * x: radius
     * y: polar / inclinitation angle (rotation around y axis)
     * z: azimuth angle (height above ground)
     */
    static cartesianToSphericalCoordinates(cartesianPoint, optionalTarget) {
        if (!optionalTarget) {
            optionalTarget = v3();
        }
        var radius = cartesianPoint.length();
        var theta = Math.atan2(cartesianPoint.x, cartesianPoint.z);
        var phi = Math.atan2(Math.sqrt(cartesianPoint.x * cartesianPoint.x + cartesianPoint.z * cartesianPoint.z), cartesianPoint.y);
        optionalTarget.x = radius;
        optionalTarget.y = theta;
        optionalTarget.z = phi;
        return optionalTarget;
    }

    /**
     * @deprecated use THREE.Spherical instead!
     * Wandelt Polarkoordinaten in kartesische Koordinaten um,
     * Umkehrfunktion zu cartesianToSphericalCoordinates
     */
    static sphericalCoordinatesToCartesian(spericalPoint, optionalTarget) {
        if (!optionalTarget) {
            optionalTarget = v3();
        }
        var radius = spericalPoint.x;
        var theta = spericalPoint.y;
        var phi = spericalPoint.z;
        optionalTarget.x = radius * Math.sin(phi) * Math.sin(theta);
        optionalTarget.y = radius * Math.cos(phi);
        optionalTarget.z = radius * Math.sin(phi) * Math.cos(theta);
        return optionalTarget;
    }

    /**
     * wie vector.lerpVectors aber mit separaten alphas pro Index
     * @param v1 Start-Vector
     * @param v2 Ziel-Vector
     * @param alphaVector jeweils pro Komponente 0-1, gibt an, wie viel der Komponente von v1 in v2 überblendet werden soll
     * @param optionalTarget
     */
    static lerpVectorsPartial(v1, v2, alphaVector, optionalTarget) {
        if (!optionalTarget) {
            optionalTarget = v3();
        }
        optionalTarget.x = v1.x * (1 - alphaVector.x) + v2.x * alphaVector.x;
        optionalTarget.y = v1.y * (1 - alphaVector.y) + v2.y * alphaVector.y;
        optionalTarget.z = v1.z * (1 - alphaVector.z) + v2.z * alphaVector.z;
        return optionalTarget;
    }

    /**
     * normalisiert den angle (Bogenmaß) im Interval zwischen 0 und 2*Math.PI (360°)
     */
    static normalizeAngle(angleInRadians) {
        const fullCircle = 2 * Math.PI;
        while (angleInRadians >= fullCircle) angleInRadians -= fullCircle;
        while (angleInRadians < 0) angleInRadians += fullCircle;
        return angleInRadians;
    }

    /**
     * wandelt einen Winkel vom Grad ins Bogenmaß um
     * Ergebniss zwischen: [-Pi,Pi]
     */
    static angelDegToRad(angelInDeg) {
        let angelInRad = angelInDeg * (Math.PI / 180)
        return angelInRad;
    }

    /**
     * true, wenn jede Komponente von sizeA <= sizeB ist
     */
    static sizeFitsInto(sizeA, sizeB) {
        return sizeA.x <= sizeB.x && sizeA.y <= sizeB.y && sizeA.z <= sizeB.z;
    }

    /**
     * true, wenn das Dreieck a,b,c degeneriert ist, d.h. wenn die Fläche 0 ist bzw. alle Punkte auf einer Linie
     * liegen oder derselbe Punkt sind.
     */
    static isTriangleDegenerated(a, b, c, epsilon = EPSILON) {
        // TODO gibt es hierfür einen schnelleren Test? Kann man auf EPSILON verzichten?
        normal.subVectors(a, b).cross(edge.subVectors(c, b));
        return normal.lengthSq() < epsilon;
    }

    /**
     * Erzeugt zu einem gegebenen Einheitsvector zwei dazu orthogonale Einheitsvectoren.
     * The output vectors {U,V} are unit length and mutually perpendicular,
     * and {U,V,W} is an orthonormal basis.
     * from https://github.com/timprepscius/GeometricTools/blob/1623f4962bfaf3d5e4e42622b74664ce1b79ecf3/WildMagic5/LibMathematics/Algebra/Wm5AVector.cpp#L269
     */
    static generateComplementBasis(inputVector, outputVectorBasis1, outputVectorBasis2) {
        var vec0 = outputVectorBasis1;
        var vec1 = outputVectorBasis2;
        var vec2 = inputVector;
        var invLength;

        if (Math.abs(vec2.x) >= Math.abs(vec2.y)) {
            // vec2.x or vec2.z is the largest magnitude component, swap them
            invLength = 1.0 / Math.sqrt(vec2.x * vec2.x + vec2.z * vec2.z);
            vec0.x = -vec2.z * invLength;
            vec0.y = 0.0;
            vec0.z = +vec2.x * invLength;
            vec1.x = vec2.y * vec0.z;
            vec1.y = vec2.z * vec0.x - vec2.x * vec0.z;
            vec1.z = -vec2.y * vec0.x;
        } else {
            // vec2.y or vec2.z is the largest magnitude component, swap them
            invLength = 1.0 / Math.sqrt(vec2.y * vec2.y + vec2.z * vec2.z);
            vec0.x = 0.0;
            vec0.y = +vec2.z * invLength;
            vec0.z = -vec2.y * invLength;
            vec1.x = vec2.y * vec0.z - vec2.z * vec0.y;
            vec1.y = -vec2.x * vec0.z;
            vec1.z = vec2.x * vec0.y;
        }
    }

    /**
     * Gibt den Winkel zwischen zweier Vektoren a und b in einer durch normal definierten
     * Ebene zurück, wobei der Winkel im Uhrzeigersinn gemessen wird. Dadurch können auch
     * Winkel größer 180° ermittelt werden, während Vector3.angleTo die Richtungsinformation
     * nicht ermitteln kann (entsprechend nur Winkel von 0° bis 180°).
     */
    static angleClockwise(a, b, normal) {
        const axb = a.clone().cross(b);
        return Math.atan2(axb.length(), a.dot(b)) * Math.sign(axb.dot(normal));
    }

    /**
     * prüft, ob zwei Normalenvektoren rechtwinklig oder parallel zueinander stehen.
     * @return 0: weder noch, 1: rechtwinklig, 2: parallel
     */
    static checkOrthogonalOrParallel(aNorm, bNorm, epsilon_orthogonal = default_epsilon_orthogonal, epsilon_parallel = default_epsilon_parallel) {
        const cos = Math.abs(aNorm.dot(bNorm));
        if (cos < epsilon_orthogonal) {
            return Maths3.ORTHOGONAL;
        }
        if (cos > epsilon_parallel) {
            return Maths3.PARALLEL;
        }
        return 0;
    }

    /**
     * stehen zwei Normalenvektoren rechtwinklig zueinander?
     */
    static isOrthogonal(aNorm, bNorm, epsilon_orthogonal = default_epsilon_orthogonal) {
        const cos = Math.abs(aNorm.dot(bNorm));
        return cos < epsilon_orthogonal;
    }

    /**
     * stehen zwei Normalenvektoren parallel zueinander?
     */
    static isParallel(aNorm, bNorm, epsilon_parallel = default_epsilon_parallel) {
        const cos = Math.abs(aNorm.dot(bNorm));
        return cos > epsilon_parallel;
    }

    /**
     * Formt einen Vector in einen Normalenvektor einer Box um, und zwar in den Normalenvektor der Box-Seite,
     * welche der verlängerte Vector schneidet. Der Vector wird dabei als Ortsvektor und die Box als Einheitsbox
     * mit dem Koordinatenursprung im Zentrum gedacht.
     * @param {THREE.Vector3} boxVector
     * @returns {THREE.Vector3}
     */
    static nearestBoxFaceNormal(boxVector) {
        const v = boxVector.clone().normalize();
        for (let i=0;i<3;i++) {
            const x = v.getComponent(i);
            v.setComponent(i,x<-0.5 ? -1 : ( x>0.5 ? +1 : 0 ));
        }
        return v;
    }

    /**
     * like Matrix4.lookAt, but with direction instead of eye and target
     * @param matrix
     * @param lookDirection
     * @param up
     */
    static lookAtMatrix(matrix,lookDirection,up) {
        const x = new THREE.Vector3();
        const y = new THREE.Vector3();
        const z = lookDirection.clone();
        const te = matrix.elements;
        if ( z.lengthSq() === 0 ) {
            z.z = 1;
        }
        z.normalize();
        x.crossVectors( up, z );
        if ( x.lengthSq() === 0 ) {
            if ( Math.abs( up.z ) === 1 ) {
                z.x += 0.0001;
            } else {
                z.z += 0.0001;
            }
            z.normalize();
            x.crossVectors( up, z );
        }
        x.normalize();
        y.crossVectors( z, x );
        te[ 0 ] = x.x; te[ 4 ] = y.x; te[ 8 ] = z.x;
        te[ 1 ] = x.y; te[ 5 ] = y.y; te[ 9 ] = z.y;
        te[ 2 ] = x.z; te[ 6 ] = y.z; te[ 10 ] = z.z;
        return matrix;
    }

    /**
     * Decompose the rotation on to 2 parts:
     *
     * 1. Twist - rotation around the "direction" vector
     * 2. Swing - rotation around axis that is perpendicular to "direction" vector
     *
     * The rotation can be composed back by
     * rotation = swing * twist
     *
     * It has a singularity in case of swing close to 180 degrees rotation.
     * If the input quaternion is of non-unit length, the outputs are non-unit as well,
     * otherwise, outputs are both unit.
     *
     * from https://stackoverflow.com/questions/3684269/component-of-a-quaternion-rotation-around-an-axis
     *
     * @param {THREE.Quaternion} rotation
     * @param {THREE.Vector3} direction
     * @param {*} [swing=new THREE.Quaternion()] optional output, for reusing
     * @param {*} [twist=new THREE.Quaternion()] optional output, for reusing
     * @returns {{swing: THREE.Quaternion, twist: THREE.Quaternion}}
     */
    static swingTwistDecomposition(
            rotation,
            direction,
            swing = new THREE.Quaternion(),
            twist = new THREE.Quaternion()) {
        const p = rotation.getAxis();
        p.projectOnVector(direction);
        if (Maths3.vectorIsFinite(p)) {
            twist.set( p.x, p.y, p.z, rotation.w );
            twist.normalize();
            swing.copy(rotation).multiply(twist.clone().conjugate());
        }
        return {swing,twist};
    }

    /**
     * Berechnet den Mittelpunkt aus n vektoren
     * @param {THREE.Vector3[]} vectors
     */
    static middle(vectors) {
        const m = v3();
        vectors.forEach(v=>m.add(v));
        m.multiplyScalar(1/vectors.length);
        return m;
    }

    /**
     * Prüft, ob zwei Vectoren kolinear sind (d.h. auf einer Linie liegen)
     * @param {THREE.Vector3} a
     * @param {THREE.Vector3} b
     * @param {number} [epsilonDegree=0.01]
     */
    static colinear(a, b, epsilonDegree=0.01) {
        const angle = a.angleTo(b) * 180/Math.PI;
        return Math.abs(angle) < epsilonDegree || Math.abs(180 - angle ) < epsilonDegree;
    }

    /**
     * berechnet die Größe der BoundingBox um eine Punktwolke (2d oder 3d)
     * @param points
     * @returns {*}
     */
    static getBoxSizeOfPoints(points) {
        const B = points[0].isVector2 ? THREE.Box2 : THREE.Box3;
        return new B().setFromPoints(points).getSize();
    }

    /**
     * convertiert eine Box2 zu Box3
     * @param box
     * @param z
     * @returns {THREE.Box3}
     */
    static box2To3(box,z=1) {
        return new THREE.Box3(
            v3(box.min.x,Math.min(box.min.y,box.max.y),-z),
            v3(box.max.x,Math.max(box.min.y,box.max.y),z));
    }

    /**
     * convertiert eine Box3 zu Box2
     * @param box
     * @returns {THREE.Box2}
     */
    static box3To2(box) {
        return new THREE.Box2(Maths3.v3To2(box.min),Maths3.v3To2(box.max));
    }

    /**
     * convertiert einen v2 zu v3
     */
    static v2To3(v,z=0) {
        return v3(v.x,v.y,z);
    }

    /**
     * convertiert einen v3 zu v2
     */
    static v3To2(v) {
        return v3(v.x,v.y);
    }

    /**
     * wandelt ein beliebiges Objekt mit x,y,z Properties in einen THREE.Vector3 um
     * @param something
     * @returns {THREE.Vector3}
     */
    static toV3(something) {
        return something instanceof THREE.Vector3
            ? something
            : new THREE.Vector3(something?.x ?? 0,something?.y ?? 0,something?.z ?? 0);
    }

    /**
     * erzeugt eine Box3 von einer grid koordinate (gridCenter) und einer size
     * @param gridCenter
     * @param size
     * @param target optional
     * @returns {THREE.Box3}
     */
    static boxFromGridMinAndSize(gridCenter, size, target=new THREE.Box3()) {
        target.min.copy(size).multiply(gridCenter).negate();
        target.max.copy(target.min).add(size);
        return target;
    }

    /**
     * erzeugt eine Matrix4, welche einer Drehung um einen Punkt und eine dadurch gehende Achse um einen Winkel entspricht.
     * Die Matrix kann anschließend mit der Matrix eines Objektes multipliziert werden.
     * @param {THREE.Vector3} pivotPoint
     * @param {THREE.Vector3} axis
     * @param {number} angle
     * @returns {THREE.Matrix4}
     */
    static createPivotMatrix(pivotPoint, axis, angle) {
        const translate0 = new THREE.Matrix4().makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z);
        const translate1 = new THREE.Matrix4().makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z);
        const rotation = new THREE.Matrix4();
        return new THREE.Matrix4()
            .multiply(translate0)
            .multiply(rotation.makeRotationAxis(axis, angle))
            .multiply(translate1);
    }


}

Maths3.ORTHOGONAL = 1;
Maths3.PARALLEL = 2;

/**
 * Standardvectoren
 */
Maths3.vector = {
    zero: v3(0, 0, 0),
    x: v3(1, 0, 0),
    y: v3(0, 1, 0),
    z: v3(0, 0, 1),
    one: v3(1, 1, 1),
    xneg: v3(-1, 0, 0),
    yneg: v3(0, -1, 0),
    zneg: v3(0, 0, -1),
    oneneg: v3(-1, -1, -1),
}

/**
 * Standardplanes
 */
Maths3.plane = {
    x0: new THREE.Plane().setFromNormalAndCoplanarPoint (Maths3.vector.x, Maths3.vector.zero),
    y0: new THREE.Plane().setFromNormalAndCoplanarPoint (Maths3.vector.y, Maths3.vector.zero),
    z0: new THREE.Plane().setFromNormalAndCoplanarPoint (Maths3.vector.z, Maths3.vector.zero),
}

/**
 * Standardquaternions
 */
Maths3.quaternion = {
    zero: new THREE.Quaternion(0, 0, 0, 1),
    x180: new THREE.Quaternion(1, 0, 0, 0),
    y180: new THREE.Quaternion(0, 1, 0, 0),
    z180: new THREE.Quaternion(0, 0, 1, 0),
    x45: quat(1, 0, 0, 45),
    y45: quat(0, 1, 0, 45),
    z45: quat(0, 0, 1, 45),
    x90: quat(1, 0, 0, 90),
    y90: quat(0, 1, 0, 90),
    z90: quat(0, 0, 1, 90),
    x270: quat(1, 0, 0, 270),
    y270: quat(0, 1, 0, 270),
    z270: quat(0, 0, 1, 270),
}

Maths.matrix = {
    identity: new THREE.Matrix4()
}
